/*
Caution: this must NOT unconditionally import large unnecessary dependencies,
such as views, neither directly nor indirectly. This is imported by the browser
entry point, and must be kept as small as possible. Large dependencies must be
conditional / lazy / on-demand / route-based.
*/

import * as a from '@mitranim/js/all.mjs'
import * as u from './util.mjs'
import * as c from './conf.mjs'
import * as en from './env.mjs'
import * as ct from './ctx.mjs'
import * as d from './dat.mjs'
import * as ro from './route.mjs'

export class ClientEnv extends en.Env {
  constructor(val) {
    super(val)

    /*
    Registry for components of our client app that must perform asynchronous
    initialization before the app is considered "ready". At the time of
    writing, this is needed only for testing.
    */
    this.initDelay = u.plist()

    this.unlisten = new u.DeinitQue()

    // See `./ads.js`.
    this.gptSizes = undefined
    this.gaTimer = undefined

    // Un-cancelable "background" context.
    this.bg = this.makeCtx(``)

    /*
    Used by `GlobalLoader`.

    In the past, the `env` object was observable, and the global loader simply
    observed the property `env.pending`. Making `env` observable created a
    hidden problem: it was causing reactive view components to accidentally
    subscribe to `env` and re-render before each pushstate transition, when
    we reassigned `env.pending`. Using a dedicated observable avoids that.
    */
    this.transitionObs = a.obs({val: 0})

    this.docHead = new a.ChildDiff(a.filter(document.head.childNodes, u.isElemHeadAuto))
  }

  static {a.memGet(this)}
  get dat() {return new d.Dat(this.bg)}
  get cook() {return new u.ClientCookies()}
  get events() {return new u.EventLifo()}
  get ana() {return new u.ClientAna(this.bg)}

  get filestack() {
    if (c.FILESTACK_ENABLED) return new u.ClientFilestack()
    return super.filestack
  }

  navigatorLangs() {return navigator.languages}
  get browserLang() {return navigator?.language || c.LANG_DEFAULT}

  init() {
    /*
    Passing a bound function, rather than implementing `.handleEvent` on the
    current object and passing the object, is a workaround for evil 3rd party
    scripts. See `readme.md` → gotchas → event listeners.
    */
    this.unlisten.add(a.eventListen({
      src: window, type: `popstate`, han: this.onPopstate.bind(this),
    }))

    this.unlisten.add(a.eventListen({
      src: window,
      type: `keydown`,
      han: function onKeydown() {
        document.body.classList.add(`enable-focus-indicators`)
      },
    }))

    this.unlisten.add(a.eventListen({
      src: window,
      type: `mousemove`,
      han: function onMousemove() {
        document.body.classList.remove(`enable-focus-indicators`)
      },
    }))

    /*
    Temporarily disabled because doesn't work in newer version of Gtag / GA.

      this.unlisten.add(a.eventListen({
        src: window,
        type: `beforeunload`,
        han: this.gtag.sendBye.bind(this.gtag),
        opt: {once: true},
      })),
    */

    const ctx = this.makeCtx(window.location)
    ctx.initData(globalThis.process?.env?.data) // See `templates.mjs`.
    this.setCurrent(ctx)
  }

  locPush(link, opt) {return this.locUpdate(link, u.historyPush, opt)}
  locReplace(link, opt) {return this.locUpdate(link, u.historyReplace, opt)}
  locPushQuery(link, opt) {return this.locPush(this.ctx.url.withQuery(link), opt)}
  locPushPatchQuery(link, opt) {return this.locPush(this.ctx.url.withQueryPatch(link), opt)}

  locUpdate(loc, fun, opt) {
    u.reqUrlLike(loc)
    a.reqFun(fun)

    loc = new URL(loc, window.location)
    if (loc.href === window.location.href) return false

    fun(loc)
    return this.rerouteWithSideEffects(opt)
  }

  async redir(loc) {
    u.reqUrlLike(loc)
    if (ro.routeExists(loc)) this.locPush(loc)
    else u.hardRedirect(loc)
  }

  async updateCurrentPage() {await this.reroute()}

  // WTB better name!
  async rerouteWithSideEffects({isPop, noscroll} = {}) {
    const url = this.ctx.url
    if (!(await this.reroute())) return false
    if (!noscroll) u.scrollOnLocationChange(url, this.ctx.url, isPop)
    return true
  }

  // Returns `true` if transition was successful.
  async reroute() {
    const prev = this.ctx?.url
    const next = window.location

    if (u.isLocHashChange(prev, next)) return true

    const ctx = this.makeCtx(next)
    this.setPending(ctx)
    try {return await this.transition(ctx)}
    finally {this.unsetPending(ctx)}
  }

  async transition(ctx) {
    this.transitionObs.val++
    const cls = `page-transition`
    const clsList = document.body.classList
    clsList.add(cls)

    try {
      const res = a.optInst((await ctx.rou.res()), Response)
      if (ctx.signal.aborted) return false

      if (a.isStatusRedir(res?.status)) {
        return this.locReplace(res.headers.get(`location`))
      }

      this.setCurrent(ctx)
      this.transitionMutDoc(res)
      this.ana.onPageView()
      return true
    }
    finally {
      this.transitionObs.val--
      this.unsetPending(ctx)
      if (!this.pending) clsList.remove(cls)
    }
  }

  transitionMutDoc(res) {
    a.optInst(res, Response)
    if (!res) return
    this.docHead.apply(document.head, u.ren.chi(document.head, res.html?.head))
    u.E(ensureRoot(), {}, res.html?.body)
  }

  onPopstate() {this.rerouteWithSideEffects({isPop: true})}

  makeCtx(loc) {
    const rou = ro.ReqRou.from(loc)
    const ctx = new ct.Ctx(this, rou)
    rou.withCtx(ctx)
    return ctx
  }

  /*
  TODO consider validating that the input is included in the list of supported
  langs.
  */
  setLang(lang) {
    a.reqStr(lang)
    this.cook.setVal(c.COOKIE_NAME_LANG, lang)
    this.lang = lang
  }

  userAgent() {return navigator.userAgent}
}

export function ensureRoot() {return document.body}
